import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function AuthorDashboard({ user, onBack, onViewStory, onUserUpdate }) {
  const [myStories, setMyStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorInfo, setAuthorInfo] = useState(null);
  const [penName, setPenName] = useState('');
  const [penNameInput, setPenNameInput] = useState('');
  const [editingPenName, setEditingPenName] = useState(false);
  const [savingPenName, setSavingPenName] = useState(false);
  const [penNameMessage, setPenNameMessage] = useState(null);

  const fetchMyStories = useCallback(async () => {
    if (!user) return;
    try {
      const authorName = user.pen_name || user.pet_name || user.display_name || 'Anonymous';
      const response = await axios.get(`${API_URL}/authors/${encodeURIComponent(authorName)}/stories`, {
        withCredentials: true
      });
      if (response.data.success) {
        setMyStories(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching author stories:', err);
      setError('Failed to load your stories');
    }
  }, [user]);

  const fetchAuthorInfo = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/authors`, { withCredentials: true });
      if (response.data.success) {
        const authorName = user.pen_name || user.pet_name || user.display_name || 'Anonymous';
        const info = response.data.data.find(a => a.author === authorName);
        setAuthorInfo(info || { story_count: 0, total_likes: 0, total_views: 0 });
      }
    } catch (err) {
      console.error('Error fetching author info:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setPenName(user.pen_name || '');
      setPenNameInput(user.pen_name || '');
    }
  }, [user]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchMyStories(), fetchAuthorInfo()]);
      setLoading(false);
    };
    loadData();
  }, [fetchMyStories, fetchAuthorInfo]);

  const handleSavePenName = async () => {
    setSavingPenName(true);
    setPenNameMessage(null);
    try {
      const response = await axios.post(`${API_URL}/auth/pen-name`, 
        { penName: penNameInput.trim() },
        { withCredentials: true }
      );
      if (response.data.success) {
        setPenName(penNameInput.trim());
        setEditingPenName(false);
        setPenNameMessage({ type: 'success', text: '✅ Pen name updated successfully!' });
        if (onUserUpdate && response.data.user) {
          onUserUpdate(response.data.user);
        }
        setTimeout(() => setPenNameMessage(null), 3000);
      }
    } catch (err) {
      console.error('Error saving pen name:', err);
      setPenNameMessage({ type: 'error', text: '❌ Failed to update pen name' });
    } finally {
      setSavingPenName(false);
    }
  };

  const getDisplayAuthorName = () => {
    if (penName) {
      return `${penName} (${user.pet_name})`;
    }
    return user.pet_name || user.display_name || 'Author';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Recent';
      return date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch {
      return 'Recent';
    }
  };

  if (!user) {
    return (
      <div className="author-dashboard">
        <div className="author-error">
          <h2>🔒 Please Sign In</h2>
          <p>You need to be logged in to view your author dashboard.</p>
          <button className="author-back-btn" onClick={onBack}>← Back to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="author-dashboard">
      <div className="author-header">
        <button className="author-back-btn" onClick={onBack}>← Back to Home</button>
        <h1>✍️ My Author Dashboard</h1>
      </div>

      {/* Author Profile Card */}
      <div className="author-profile-card">
        <div className="author-avatar-section">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.pet_name} className="author-avatar" />
          ) : (
            <div className="author-avatar-placeholder">🐾</div>
          )}
          <div className="author-info">
            <h2>{getDisplayAuthorName()}</h2>
            <p className="author-email">{user.email || ''}</p>

            {/* Pen Name Editor */}
            <div className="author-pen-name-section">
              {editingPenName ? (
                <div className="author-pen-name-edit">
                  <input
                    type="text"
                    value={penNameInput}
                    onChange={(e) => setPenNameInput(e.target.value)}
                    placeholder="Enter your pen name..."
                    className="author-pen-name-input"
                    maxLength="50"
                    autoFocus
                  />
                  <div className="author-pen-name-actions">
                    <button 
                      className="author-pen-name-save-btn"
                      onClick={handleSavePenName}
                      disabled={savingPenName}
                    >
                      {savingPenName ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      className="author-pen-name-cancel-btn"
                      onClick={() => {
                        setPenNameInput(penName);
                        setEditingPenName(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  <p className="author-pen-name-hint">
                    Stories will be published as: <strong>{penNameInput ? `${penNameInput} (${user.pet_name})` : user.pet_name}</strong>
                  </p>
                </div>
              ) : (
                <div className="author-pen-name-display">
                  {penName ? (
                    <span className="author-pen-name-current">
                      ✍️ Pen name: <strong>{penName}</strong>
                      <button className="author-pen-name-edit-btn" onClick={() => setEditingPenName(true)}>✏️ Edit</button>
                    </span>
                  ) : (
                    <span className="author-pen-name-current">
                      <button className="author-pen-name-set-btn" onClick={() => setEditingPenName(true)}>
                        ✍️ Set a pen name
                      </button>
                      <span className="author-pen-name-current-label">
                        (Currently writing as <strong>{user.pet_name}</strong>)
                      </span>
                    </span>
                  )}
                </div>
              )}
              {penNameMessage && (
                <div className={`author-pen-name-message ${penNameMessage.type}`}>
                  {penNameMessage.text}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="author-stats-row">
          <div className="author-stat">
            <span className="author-stat-number">{myStories.length}</span>
            <span className="author-stat-label">Stories Written</span>
          </div>
          <div className="author-stat">
            <span className="author-stat-number">{authorInfo?.total_likes || 0}</span>
            <span className="author-stat-label">Total Likes</span>
          </div>
          <div className="author-stat">
            <span className="author-stat-number">{authorInfo?.total_views || 0}</span>
            <span className="author-stat-label">Total Views</span>
          </div>
        </div>
      </div>

      {/* My Stories */}
      <div className="author-section">
        <h2>📖 My Stories</h2>
        {loading ? (
          <div className="author-loading">Loading your stories...</div>
        ) : error ? (
          <div className="author-error-message">{error}</div>
        ) : myStories.length === 0 ? (
          <div className="author-empty">
            <div className="author-empty-icon">✍️</div>
            <h3>No stories yet</h3>
            <p>You haven't written any stories yet. Start writing your first story!</p>
          </div>
        ) : (
          <div className="author-stories-grid">
            {myStories.map(story => (
              <div 
                key={story.story_id || story.id} 
                className="author-story-card"
                onClick={() => onViewStory(story.story_id || story.id)}
              >
                <div className="author-story-card-content">
                  <h3>{story.title || 'Untitled'}</h3>
                  <p className="author-story-excerpt">
                    {story.content_text?.substring(0, 150) || 'No preview available'}...
                  </p>
                  <div className="author-story-meta">
                    <span>❤️ {story.like_count || 0}</span>
                    <span>👁️ {story.view_count || 0}</span>
                    <span>💬 {story.comment_count || 0}</span>
                    <span>📅 {formatDate(story.created_at)}</span>
                  </div>
                  {story.genre && (
                    <span className="author-story-genre">{story.genre}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthorDashboard;